import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import {
  resolveLocalhostAdminLoginReturnUrl,
  resolveLocalhostAutoLoginUserId,
  shouldRepairLocalhostAdminSession,
} from "@/auth/localAdminAutoLogin";
import { isLocalhostHostname } from "@/components/ui/apiHealthGateUtils";
import { useAdminLogout } from "@/hooks/useAdminLogout";

const LOCAL_ADMIN_SESSION_REPAIR_KEY =
  "hc-admin-local-session-repair-attempted";

const hasRepairAlreadyBeenAttempted = (): boolean => {
  try {
    return (
      window.sessionStorage.getItem(LOCAL_ADMIN_SESSION_REPAIR_KEY) === "1"
    );
  } catch {
    return false;
  }
};

const setRepairAttempted = (attempted: boolean): void => {
  try {
    if (attempted) {
      window.sessionStorage.setItem(LOCAL_ADMIN_SESSION_REPAIR_KEY, "1");
    } else {
      window.sessionStorage.removeItem(LOCAL_ADMIN_SESSION_REPAIR_KEY);
    }
  } catch {
    // A storage-disabled browser can still complete the in-memory attempt.
  }
};

export const useLocalAdminSessionRepair = (args: {
  claimCheckFailed: boolean;
  hasAdminClaim: boolean;
  isCheckingClaims: boolean;
  userId: string | undefined;
}) => {
  const location = useLocation();
  const testUserId = resolveLocalhostAutoLoginUserId(
    import.meta.env.VITE_LOCALHOST_TEST_USER_FIREBASE_UUID,
  );
  const returnUrl =
    location.pathname === "/login"
      ? "/dashboard"
      : resolveLocalhostAdminLoginReturnUrl({
          hash: location.hash,
          pathname: location.pathname,
          search: location.search,
        });
  const { logout, isLoggingOut } = useAdminLogout({ redirectTo: returnUrl });
  const [isRepairRunning, setIsRepairRunning] = useState(false);
  const shouldRepair = shouldRepairLocalhostAdminSession({
    ...args,
    isLocalhost:
      typeof window !== "undefined" &&
      isLocalhostHostname(window.location.hostname),
    repairAlreadyAttempted:
      typeof window !== "undefined" && hasRepairAlreadyBeenAttempted(),
    testUserId,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (args.hasAdminClaim) {
      setRepairAttempted(false);
      return;
    }
    if (!shouldRepair) return;

    setRepairAttempted(true);
    setIsRepairRunning(true);
    void logout().finally(() => {
      setIsRepairRunning(false);
    });
  }, [args.hasAdminClaim, logout, shouldRepair]);

  return {
    isLoggingOut,
    isRepairingLocalSession: shouldRepair || isRepairRunning,
    logout,
  };
};
